import { motion } from "framer-motion";
import { ArrowUpRight, Check, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { SCHOOL_CAMPS, SITE } from "../lib/site";

// Each camp moves to "completed" at local midnight after its date.
const UPCOMING_CAMPS = [
  { id: "up-nov8", date: "2026-11-08", dates: "Nov 8", school: "St. Clair" },
  { id: "up-nov9", date: "2026-11-09", dates: "Nov 9", school: "Cleveland" },
  { id: "up-nov13", date: "2026-11-13", dates: "Nov 13", school: "Lakeview, MN" },
  { id: "up-nov14", date: "2026-11-14", dates: "Nov 14", school: "Willmar, MN" },
  { id: "up-nov15", date: "2026-11-15", dates: "Nov 15", school: "Minnesota Comets AAU Program" },
  { id: "up-nov20", date: "2026-11-20", dates: "Nov 20", school: "Loyola" },
];

const isPast = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(`${d}T00:00:00`) < today;
};

export default function SchoolCamps() {
  const camps = SCHOOL_CAMPS;
  const months = ["Fall", "June", "July", "August", "Summer"];
  const upcoming = UPCOMING_CAMPS.filter((c) => !isPast(c.date));
  const completed = [
    ...UPCOMING_CAMPS.filter((c) => isPast(c.date)).map((c) => ({ ...c, month: "Fall" })),
    ...camps.schedule,
  ];

  return (
    <div data-testid="school-camps-page" className="pt-28 md:pt-32">
      {/* HEADER */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">For Schools & Programs</span>
          </div>
          <div className="grid grid-cols-12 gap-6 items-end">
            <h1 className="col-span-12 md:col-span-8 font-display uppercase text-6xl md:text-8xl leading-[0.85] tracking-tight">
              School <br /><span className="text-primary">Camps.</span>
            </h1>
            <p className="col-span-12 md:col-span-4 text-foreground/75 text-base md:text-lg">{camps.intro}</p>
          </div>
        </div>
      </section>

      {/* COACH TESTIMONIAL */}
      <section className="pb-16 md:pb-20" data-testid="schools-coach-testimonial">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="relative border-l-4 border-primary bg-foreground text-background p-8 md:p-12"
          >
            <div className="absolute top-6 right-6 md:top-8 md:right-10 font-display text-7xl md:text-9xl leading-none text-primary/30 select-none">”</div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">From a Host Coach</span>
            </div>
            <blockquote className="font-display uppercase text-2xl md:text-4xl leading-[1.05] tracking-tight max-w-4xl">
              “BeGreat Training just wrapped up two amazing days at our facility and our students in grades 3–12 showed UP!”
            </blockquote>
            <p className="mt-6 max-w-3xl text-background/80 text-base md:text-lg leading-relaxed">
              A huge THANK YOU to the BeGreat Training team for pouring into our kids. As coaches, we saw incredible improvements last year and we cannot wait to see the growth next season! We love having them here so much that we’re already <span className="text-primary font-semibold">booking them for year 3</span>.
            </p>
            <figcaption className="mt-7 text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-background/60">
              — Local Host Coach
            </figcaption>
          </motion.figure>
        </div>
      </section>

      {/* WHAT BEGREAT BRINGS */}
      <section className="pb-16 md:pb-24" data-testid="schools-collaboration">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-12 gap-x-0 gap-y-8 md:gap-12 items-start"
          >
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The Collaboration</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Built <span className="text-primary">around</span> your program.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p className="text-foreground/85 text-base md:text-lg leading-relaxed">
                BeGreat comes to your school with both a skill enhancement system and an in-game approach — working with coaches on the specific needs of your program.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Offensive installs",
                  "Defensive installs",
                  "Practice planning",
                  "Skill-targeted small-sided games",
                  "And much more",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base">
                    <span className="mt-1 shrink-0 bg-primary text-primary-foreground p-1"><Check size={12} strokeWidth={3} /></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-foreground/70 text-base md:text-lg leading-relaxed">
                We make this a <span className="font-semibold text-foreground">collaborative experience</span> based on each school's needs.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-x-0 gap-y-8 md:gap-12">
          <div className="col-span-12 md:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">What You Get</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mb-8">
              A full BeGreat <br />experience — <br /><span className="text-primary">at your gym.</span>
            </h2>
            <ul className="space-y-3">
              {camps.whatYouGet.map((line) => (
                <li key={line} className="flex items-start gap-3 text-base">
                  <span className="mt-1 shrink-0 bg-primary text-primary-foreground p-1"><Check size={12} strokeWidth={3} /></span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-3">
            {camps.gallery.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`camp-photo-${g.id}`}
                className={`relative overflow-hidden bg-foreground ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
              >
                <img
                  src={g.src}
                  alt=""
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPS COMING UP */}
      {upcoming.length > 0 && (
      <section className="py-16 md:py-24" data-testid="camps-upcoming">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-12 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">On The Calendar</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Camps <span className="text-primary">coming up.</span>
              </h2>
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-muted-foreground max-w-xs md:text-right flex items-center gap-2">
              <Calendar size={14} className="text-primary" /> {upcoming.length} {upcoming.length === 1 ? "camp" : "camps"} on deck
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {upcoming.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`camp-${c.id}`}
                className="group relative p-5 md:p-6 border border-foreground bg-foreground text-background hover:bg-primary hover:border-primary transition-colors h-full"
              >
                <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground px-2 py-1 text-[9px] font-bold uppercase tracking-[0.25em]">
                  Upcoming
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2 text-primary group-hover:text-primary-foreground/90">
                  {c.dates}
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl leading-[0.95]">{c.school}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SUMMER 2026 COMPLETED BANNER */}
      <section className="py-14 md:py-20 bg-primary text-primary-foreground" data-testid="summer-completed">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-2 flex md:justify-center">
            <Check size={56} strokeWidth={2.5} />
          </div>
          <div className="col-span-12 md:col-span-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary-foreground/80 mb-3">Wrapped</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
              Summer 2026 camps: <span className="underline decoration-[3px] underline-offset-[6px]">completed.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-primary-foreground/90 max-w-3xl">
              Thank you to every school and program that hosted BeGreat this summer. Here's who we worked with.
            </p>
          </div>
        </div>
      </section>

      {/* SUMMER 2026 RECAP */}
      <section className="py-20 md:py-28" data-testid="summer-recap">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-12 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">2026 Recap</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                {completed.length} Camps <br /><span className="text-primary">completed.</span>
              </h2>
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-muted-foreground max-w-xs md:text-right flex items-center gap-2">
              <Check size={14} className="text-primary" strokeWidth={3} /> {completed.length} camps completed
            </div>
          </div>

          {months.map((m) => {
            const list = completed.filter((c) => c.month === m);
            if (!list.length) return null;
            return (
              <div key={m} className="mb-12 last:mb-0" data-testid={`schedule-month-${m.toLowerCase()}`}>
                <div className="flex items-baseline gap-4 mb-5 pb-3 border-b border-foreground/15">
                  <div className="font-display text-5xl md:text-7xl uppercase leading-none text-foreground/15">{m}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">{list.length} {list.length === 1 ? "Camp" : "Camps"} · Wrapped</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {list.map((c, i) => {
                    const Inner = (
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        data-testid={`camp-${c.id}`}
                        className="group relative p-5 md:p-6 border border-foreground/15 bg-secondary/40 hover:bg-foreground hover:text-background hover:border-foreground transition-colors h-full"
                      >
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground px-2 py-1 text-[9px] font-bold uppercase tracking-[0.25em] transition-colors">
                          <Check size={10} strokeWidth={3} /> Completed
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2 text-primary">
                          {c.dates}
                        </div>
                        <div className="font-display uppercase text-2xl md:text-3xl leading-[0.95] pr-24">{c.school}</div>
                      </motion.div>
                    );
                    return c.link ? (
                      <Link key={c.id} to={c.link} className="block">{Inner}</Link>
                    ) : (
                      <div key={c.id}>{Inner}</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-14 md:py-20 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 items-center">
          <h3 className="col-span-12 md:col-span-8 font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
            Want BeGreat at <span className="text-primary">your school?</span>
          </h3>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <a
              href={`mailto:${SITE.email}?subject=School Camp Inquiry`}
              data-testid="schools-email-cta"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-background text-xs font-bold uppercase tracking-[0.25em] hover:bg-primary hover:border-primary transition-colors"
            >
              Reach Out <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
